import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import blogArticles, { seoEnhancedArticles } from "../data/blogArticles";
import RelatedArticles from "../components/blog/RelatedArticles";
import ShareButtons from "../components/blog/ShareButtons";
import GridOverlay from "../components/ui/GridOverlay";
import ArticleContainer from "../components/blog/ArticleContainer";

const BlogDetail = () => {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [seoArticle, setSeoArticle] = useState(null);
    const [relatedArticles, setRelatedArticles] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fonction pour obtenir la couleur en fonction de la catégorie
    const getCategoryColor = (category) => {
        const colorMap = {
            "Design Web": "#4A6FFF",
            "SEO": "#FF6B35",
            "Développement Mobile": "#8A4FFF",
            "Innovation": "#00BFB2",
            "E-commerce": "#FF4A6F",
            "Développement Web": "#3772FF",
            "Marketing Digital": "#FF9F1C",
            "Cybersécurité": "#D62828",
            "Transformation Digitale": "#558B6E",
            "Green IT": "#57A773"
        };

        return colorMap[category] || "#6B46C1"; // Utilise la couleur purple de base si la catégorie n'est pas trouvée
    };

    useEffect(() => {
        window.scrollTo(0, 0);

        const currentArticle = blogArticles.find(
            (article) => article.slug === slug
        );
        const currentSeoArticle = seoEnhancedArticles.find(
            (article) => article.slug === slug
        );

        if (currentArticle) {
            setArticle(currentArticle);
            setSeoArticle(currentSeoArticle);

            // Find related articles based on category and tags
            const related = blogArticles
                .filter(
                    (a) =>
                        a.id !== currentArticle.id &&
                        (a.category === currentArticle.category ||
                            a.tags.some((tag) =>
                                currentArticle.tags.includes(tag)
                            ))
                )
                .slice(0, 3);

            setRelatedArticles(related);
        }

        setLoading(false);
    }, [slug]);

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!article) {
        return (
            <div className="min-h-screen flex flex-col justify-center items-center px-4">
                <h1 className="text-3xl font-bold text-gray-800 mb-4">
                    Article introuvable
                </h1>
                <p className="text-gray-600 mb-8">
                    L'article que vous recherchez n'existe pas ou a été déplacé.
                </p>
                <Link
                    to="/blog"
                    className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                    Retour au blog
                </Link>
            </div>
        );
    }

    const formattedDate = new Date(article.date).toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    // Convertir le contenu Markdown en HTML "manuellement" pour le SEO
    const formatContentToHTML = (content) => {
        // Convertir les titres
        const htmlContent = content
            .replace(
                /^# (.+)$/gm,
                '<h1 class="text-4xl font-bold mb-6 mt-10 text-gray-800">$1</h1>'
            )
            .replace(
                /^## (.+)$/gm,
                '<h2 class="text-3xl font-semibold mb-5 mt-8 text-gray-800">$1</h2>'
            )
            .replace(
                /^### (.+)$/gm,
                '<h3 class="text-2xl font-semibold mb-4 mt-7 text-gray-800">$1</h3>'
            )
            // Convertir les paragraphes
            .replace(
                /^(?!<h[1-6]|<ul|<ol|<li|<blockquote|<pre|<table)(.+)$/gm,
                '<p class="mb-5 text-gray-700 leading-relaxed">$1</p>'
            )
            // Convertir les listes
            .replace(/^- (.+)$/gm, '<li class="ml-6 mb-3 text-gray-700">$1</li>')
            .replace(
                /(<li[^>]*>.*<\/li>\n)+/g,
                '<ul class="list-disc mb-6 pl-5">$&</ul>'
            )
            // Convertir les liens vers les images internes avec lazy loading pour de meilleures performances
            .replace(
                /!\[(.+)\]\((.+)\)/g,
                '<img src="$2" alt="$1" class="w-full rounded-lg mb-8 shadow-md" loading="lazy">'
            )
            // Convertir les liens
            .replace(
                /\[(.+)\]\((.+)\)/g,
                '<a href="$2" class="text-blue-600 font-medium hover:underline transition-colors">$1</a>'
            );

        return htmlContent;
    };

    const renderedContent = formatContentToHTML(article.content);
    const categoryColor = getCategoryColor(article.category);

    return (
        <main className="min-h-[120vh] pt-[70px] bg-white">
            <Helmet>
                <title>{article.title} | Blog NYLORIA</title>
                <meta name="description" content={article.metaDescription} />
                <meta name="keywords" content={article.tags.join(", ")} />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="article" />
                <meta property="og:title" content={article.title} />
                <meta property="og:description" content={article.summary} />
                <meta
                    property="og:image"
                    content={`https://www.nyloria.fr${article.image}`}
                />
                <meta
                    property="og:url"
                    content={`https://www.nyloria.fr/blog/${article.slug}`}
                />
                <meta property="og:site_name" content="NYLORIA Blog" />
                <meta
                    property="article:published_time"
                    content={new Date(article.date).toISOString()}
                />
                <meta property="article:section" content={article.category} />
                {article.tags.map((tag, index) => (
                    <meta key={index} property="article:tag" content={tag} />
                ))}

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={article.title} />
                <meta name="twitter:description" content={article.summary} />
                <meta
                    name="twitter:image"
                    content={`https://www.nyloria.fr${article.image}`}
                />

                {/* Canonical URL */}
                <link
                    rel="canonical"
                    href={`https://www.nyloria.fr/blog/${article.slug}`}
                />

                {/* Schema.org JSON-LD */}
                <script type="application/ld+json">
                    {JSON.stringify(seoArticle?.schemaMarkup || {})}
                </script>
            </Helmet>

            <GridOverlay />

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sticky z-10">
                <Link
                    to="/blog"
                    className="inline-flex items-center hover:underline mb-8 text-gray-600 transition-colors hover:text-gray-900"
                >
                    <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M10 19l-7-7m0 0l7-7m-7 7h18"
                        ></path>
                    </svg>
                    Retour aux articles
                </Link>

                <motion.header
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-12"
                >
                    <span
                        className="inline-block px-3 py-1 text-sm font-medium rounded-full mb-3"
                        style={{
                            backgroundColor: `${categoryColor}20`,
                            color: categoryColor
                        }}
                    >
                        {article.category}
                    </span>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                        {article.title}
                    </h1>

                    <div className="flex items-center space-x-4 text-gray-600 text-sm">
                        <div className="flex items-center">
                            <span className="font-medium">
                                Par {article.author}
                            </span>
                        </div>
                        <span>•</span>
                        <time dateTime={article.date}>{formattedDate}</time>
                    </div>
                </motion.header>

                {article.image && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="mb-16 sticky z-10 relative"
                    >
                        <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-auto rounded-xl shadow-lg object-cover max-h-[500px]"
                            width="1200"
                            height="630"
                            fetchpriority="high"
                            loading="lazy"
                        />
                    </motion.div>
                )}

                <motion.div
                    className="flex flex-col lg:flex-row gap-16"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <article className="lg:w-3/4 max-w-none">
                        <div
                            className="article-content prose prose-lg prose-headings:font-bold prose-headings:text-gray-800 prose-p:text-gray-600 prose-p:leading-relaxed prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-img:rounded-lg prose-img:shadow-md"
                            dangerouslySetInnerHTML={{
                                __html: renderedContent,
                            }}
                        />
                    </article>

                    <aside className="lg:w-1/4">
                        <div className="sticky top-24 space-y-8">
                            <div className="bg-white p-6 rounded-xl shadow-sm">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    Catégorie
                                </h3>
                                <Link
                                    to={`/blog?category=${article.category}`}
                                    className="inline-block px-3 py-1 rounded-full text-sm font-medium transition-colors"
                                    style={{
                                        backgroundColor: `${categoryColor}20`,
                                        color: categoryColor
                                    }}
                                >
                                    {article.category}
                                </Link>
                            </div>

                            <div className="bg-white p-6 rounded-xl shadow-sm">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    Tags
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {article.tags.map((tag, index) => (
                                        <Link
                                            key={index}
                                            to={`/blog?tag=${tag}`}
                                            className="bg-gray-50 text-gray-500 px-3 py-1 rounded-full text-sm hover:bg-gray-100 transition-colors"
                                        >
                                            {tag}
                                        </Link>
                                    ))}
                                </div>
                            </div>


                            <ShareButtons
                                title={article.title}
                                url={`/blog/${article.slug}`}
                            />
                        </div>
                    </aside>
                </motion.div>

                {relatedArticles.length > 0 && (
                    <div className="mt-20 pt-10 border-t border-gray-200">
                        <h2 className="text-2xl font-bold text-gray-900 mb-10">
                            Articles similaires
                        </h2>
                                                <ArticleContainer
                            articles={relatedArticles}
                            isNewArticle={false}
                        />
                    </div>
                )}
            </div>
        </main>
    );
};

export default BlogDetail;